import { ILogger } from '@cian/logger';

const CLS_DIAGNOSTICS_DOMAIN = 'cls-diagnostics';
const CLS_DIAGNOSTICS_MESSAGE = 'newbuilding_search_cls_diagnostic';
const CLS_DIAGNOSTICS_MIN_VALUE = 0.01;
const CLS_DIAGNOSTICS_SAMPLE_RATE = 1;
const MAX_SOURCE_COUNT = 5;
const MAX_STRING_LENGTH = 300;
const MAX_SOURCE_TEXT_LENGTH = 150;
const MAX_ANCESTOR_COUNT = 5;

interface IStartClsDiagnosticsParams {
  logger: ILogger;
  sampleRate?: number;
  random?: () => number;
  win?: Window;
}

interface IWindowWithPerformanceObserver extends Window {
  PerformanceObserver?: typeof PerformanceObserver;
}

interface ILayoutShiftSource {
  node?: Node;
  previousRect?: DOMRectReadOnly;
  currentRect?: DOMRectReadOnly;
}

interface ILayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean;
  sources?: ILayoutShiftSource[];
  value: number;
}

interface ISerializedRect {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
  x: number;
  y: number;
}

export function startClsDiagnostics({
  logger,
  sampleRate = CLS_DIAGNOSTICS_SAMPLE_RATE,
  random = Math.random,
  win = window,
}: IStartClsDiagnosticsParams): PerformanceObserver | undefined {
  const PerformanceObserverCtor = (win as IWindowWithPerformanceObserver).PerformanceObserver;

  if (!PerformanceObserverCtor || random() >= sampleRate) {
    return undefined;
  }

  let pageClsValue = 0;
  let entryIndex = 0;

  const observer = new PerformanceObserverCtor((list: PerformanceObserverEntryList) => {
    list.getEntries().forEach((entry: PerformanceEntry) => {
      const layoutShiftEntry = entry as ILayoutShiftEntry;

      if (!layoutShiftEntry.hadRecentInput) {
        pageClsValue += layoutShiftEntry.value;
        entryIndex += 1;
      }

      logLayoutShift(logger, win, layoutShiftEntry, pageClsValue, entryIndex);
    });
  });

  observer.observe({ type: 'layout-shift', buffered: true });

  return observer;
}

function logLayoutShift(
  logger: ILogger,
  win: Window,
  entry: ILayoutShiftEntry,
  pageClsValue: number,
  entryIndex: number,
) {
  if (entry.hadRecentInput || entry.value < CLS_DIAGNOSTICS_MIN_VALUE) {
    return;
  }

  const headerRect = getElementRect(win, '#header-frontend');
  const rootRect = getElementRect(win, '#newbuilding-search-frontend');
  const sources = (entry.sources || []).slice(0, MAX_SOURCE_COUNT).map(serializeSource);
  const rootSource = sources.find(source => source.nodeId === 'newbuilding-search-frontend');
  const navigationTiming = getNavigationTiming(win);

  logger.warning(CLS_DIAGNOSTICS_MESSAGE, {
    domain: CLS_DIAGNOSTICS_DOMAIN,
    details: JSON.stringify({
      url: win.location.href,
      path: win.location.pathname,
      clsValue: roundNumber(entry.value),
      pageClsValue: roundNumber(pageClsValue),
      entryIndex,
      startTime: roundNumber(entry.startTime),
      viewportWidth: win.innerWidth,
      viewportHeight: win.innerHeight,
      visualViewportWidth: roundOptional(win.visualViewport?.width),
      visualViewportHeight: roundOptional(win.visualViewport?.height),
      screenWidth: win.screen?.width,
      screenHeight: win.screen?.height,
      devicePixelRatio: win.devicePixelRatio,
      scrollX: roundNumber(win.scrollX),
      scrollY: roundNumber(win.scrollY),
      documentReadyState: win.document.readyState,
      documentVisibilityState: win.document.visibilityState,
      scrollRestoration: win.history?.scrollRestoration,
      navigationType: navigationTiming?.type,
      navigationTiming,
      paintTiming: getPaintTiming(win),
      loadPhase: getLoadPhase(entry.startTime, navigationTiming, win.document.readyState),
      pageElements: getPageElementsDiagnostics(win),
      headerHeight: headerRect?.height,
      rootTop: rootRect?.top,
      rootSourcePreviousTop: rootSource?.previousRect?.top,
      rootSourceCurrentTop: rootSource?.currentRect?.top,
      rootSourceTopDelta:
        rootSource?.previousRect && rootSource.currentRect
          ? roundNumber(rootSource.currentRect.top - rootSource.previousRect.top)
          : undefined,
      sources,
    }),
  });
}

function getElementRect(win: Window, selector: string): ISerializedRect | undefined {
  const element = win.document.querySelector(selector);

  return element ? serializeRect(element.getBoundingClientRect()) : undefined;
}

function serializeSource(source: ILayoutShiftSource) {
  const node = source.node;
  const element = node && node.nodeType === Node.ELEMENT_NODE ? (node as Element) : undefined;
  const parentElement = element?.parentElement;

  return {
    nodeSelector: element ? getNodeSelector(element) : undefined,
    nodeTag: element?.tagName,
    nodeId: element?.id,
    nodeClassName: element ? trimString(String(element.className || '')) : undefined,
    parentSelector: parentElement ? getNodeSelector(parentElement) : undefined,
    parentNodeTag: parentElement?.tagName,
    parentNodeId: parentElement?.id,
    parentNodeClassName: parentElement ? trimString(String(parentElement.className || '')) : undefined,
    ancestorSelectors: element ? getAncestorSelectors(element) : undefined,
    sourceText: element ? trimString(normalizeText(element.textContent || ''), MAX_SOURCE_TEXT_LENGTH) : undefined,
    sourceHtml: element ? trimString(element.outerHTML, MAX_STRING_LENGTH) : undefined,
    previousRect: serializeRect(source.previousRect),
    currentRect: serializeRect(source.currentRect),
  };
}

function getNodeSelector(element: Element): string {
  const tagName = element.tagName.toLowerCase();
  const id = element.id ? `#${element.id}` : '';
  const className = String(element.className || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map(name => `.${name}`)
    .join('');

  return trimString(`${tagName}${id}${className}`);
}

function getAncestorSelectors(element: Element): string[] {
  const selectors: string[] = [];
  let currentElement = element.parentElement;

  while (currentElement && selectors.length < MAX_ANCESTOR_COUNT) {
    selectors.push(getNodeSelector(currentElement));
    currentElement = currentElement.parentElement;
  }

  return selectors;
}

function getNavigationTiming(win: Window) {
  const navigationEntry = win.performance?.getEntriesByType?.('navigation')?.[0] as
    | PerformanceNavigationTiming
    | undefined;

  if (!navigationEntry) {
    return undefined;
  }

  return {
    domContentLoadedEventEnd: roundNumber(navigationEntry.domContentLoadedEventEnd),
    domContentLoadedEventStart: roundNumber(navigationEntry.domContentLoadedEventStart),
    domInteractive: roundNumber(navigationEntry.domInteractive),
    duration: roundNumber(navigationEntry.duration),
    fetchStart: roundNumber(navigationEntry.fetchStart),
    loadEventEnd: roundNumber(navigationEntry.loadEventEnd),
    loadEventStart: roundNumber(navigationEntry.loadEventStart),
    requestStart: roundNumber(navigationEntry.requestStart),
    responseEnd: roundNumber(navigationEntry.responseEnd),
    responseStart: roundNumber(navigationEntry.responseStart),
    startTime: roundNumber(navigationEntry.startTime),
    type: navigationEntry.type,
  };
}

function getPaintTiming(win: Window) {
  const paintEntries = (win.performance?.getEntriesByType?.('paint') || []) as PerformanceEntry[];
  const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
  const firstContentfulPaint = paintEntries.find(entry => entry.name === 'first-contentful-paint');

  return {
    firstContentfulPaint: roundOptional(firstContentfulPaint?.startTime),
    firstPaint: roundOptional(firstPaint?.startTime),
  };
}

function getLoadPhase(
  startTime: number,
  navigationTiming: ReturnType<typeof getNavigationTiming>,
  readyState: DocumentReadyState,
): string {
  if (navigationTiming?.responseEnd && startTime <= navigationTiming.responseEnd) {
    return 'before_response_end';
  }

  if (navigationTiming?.domContentLoadedEventEnd && startTime <= navigationTiming.domContentLoadedEventEnd) {
    return 'before_dom_content_loaded_end';
  }

  if (navigationTiming?.loadEventEnd && startTime <= navigationTiming.loadEventEnd) {
    return 'before_load_event_end';
  }

  if (readyState !== 'complete') {
    return `document_${readyState}`;
  }

  return 'after_load';
}

function getPageElementsDiagnostics(win: Window) {
  const document = win.document;
  const offersBody = document.querySelector('[data-mark="OffersBody"], [data-id="offers_body_container"]');
  const root = document.querySelector('#newbuilding-search-frontend');

  return {
    gkCardCount: countElements(document, '[data-name="GKCardComponent"], [data-mark="GKCard"]'),
    hasOffersBody: Boolean(offersBody),
    hasSeoCrossLinks: countElements(document, '[data-testid="SeoCrossLinksV2"]') > 0,
    hasSimilarNewbuildings: countElements(document, '[data-testid="SimilarNewbuildingsWrapper"]') > 0,
    offersBodyRect: offersBody ? serializeRect(offersBody.getBoundingClientRect()) : undefined,
    rootChildElementCount: root?.children.length,
  };
}

function countElements(document: Document, selector: string): number {
  return document.querySelectorAll(selector).length;
}

function serializeRect(rect?: DOMRectReadOnly): ISerializedRect | undefined {
  if (!rect) {
    return undefined;
  }

  return {
    x: roundNumber(rect.x),
    y: roundNumber(rect.y),
    width: roundNumber(rect.width),
    height: roundNumber(rect.height),
    top: roundNumber(rect.top),
    right: roundNumber(rect.right),
    bottom: roundNumber(rect.bottom),
    left: roundNumber(rect.left),
  };
}

function roundNumber(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 1000) / 1000 : 0;
}

function roundOptional(value?: number): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 1000) / 1000 : undefined;
}

function normalizeText(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function trimString(value: string, maxLength = MAX_STRING_LENGTH): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}
